import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileText, Receipt, Mail, DollarSign, ArrowRight, ShieldCheck, AlertTriangle, CheckCircle2, CreditCard, Send } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [sys, setSys] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/catalog-health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/admin/system-status").then((r) => setSys(r.data)).catch(() => {});
  }, []);

  const cards = [
    [DollarSign, "Revenue", stats ? `$${stats.revenue.toLocaleString()}` : "—"],
    [Receipt, "Paid orders", stats?.orders ?? "—"],
    [FileText, "Documents", stats?.documents ?? "—"],
    [Mail, "Leads captured", stats?.leads ?? "—"],
  ];

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Overview</h1>
      <p className="text-muted-foreground mt-2">Your business at a glance.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {cards.map(([Icon, label, val]) => (
          <div key={label} className="bg-white border border-border p-6">
            <Icon className="w-5 h-5 text-primary mb-4" />
            <div className="font-heading font-black text-3xl">{val}</div>
            <div className="text-sm text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </div>

      {sys && <LaunchStatus sys={sys} />}

      {health && <CatalogHealth health={health} />}

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {[
          ["Upload documents", "Add and categorize your files.", "/admin/documents"],
          ["Edit products", "Adjust pricing, copy, and packaging.", "/admin/products"],
          ["View orders", "Track paid transactions & revenue.", "/admin/orders"],
        ].map(([t, d, to]) => (
          <Link key={to} to={to} className="bg-white border border-border p-6 btn-lift block">
            <h3 className="font-heading font-bold text-lg">{t}</h3>
            <p className="text-sm text-muted-foreground mt-1">{d}</p>
            <span className="text-sm font-semibold text-primary inline-flex items-center gap-1 mt-4">
              Open <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function LaunchStatus({ sys }) {
  const payLive = sys.payments?.mode === "live";
  const payTest = sys.payments?.mode === "test";
  const emailLive = sys.email?.mode === "custom" && sys.email?.configured;

  const StatusPill = ({ live, liveText, warnText, testid }) => (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 border ${
        live ? "bg-green-50 border-green-300 text-green-700" : "bg-amber-50 border-amber-300 text-amber-700"
      }`}>
      {live ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {live ? liveText : warnText}
    </span>
  );

  return (
    <div className="bg-white border border-border p-6 mt-8" data-testid="launch-status">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold text-sm">Launch status</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-5">Whether real payments and customer emails are switched on <span className="font-semibold">in this environment</span>.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex items-start gap-3 border border-border p-4" data-testid="launch-payments">
          <CreditCard className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Payments</span>
              <StatusPill live={payLive} liveText="Live" warnText={payTest ? "Test" : "Not set"} testid="payments-mode-pill" />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {payLive
                ? "Real cards are charged. You're taking live payments."
                : payTest
                ? "Stripe is in test mode — no real money moves. Set a live key in production to go live."
                : "No Stripe key detected."}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 border border-border p-4" data-testid="launch-email">
          <Send className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Customer email</span>
              <StatusPill live={emailLive} liveText="Deliverable" warnText="Sandbox" testid="email-mode-pill" />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {emailLive
                ? `Sending from ${sys.email.sender} — customers receive receipts.`
                : "Sandbox mode — receipts only reach the account owner. Verify your domain, then set SENDER_EMAIL."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CatalogHealth({ health }) {
  const inSync = health.in_sync;
  const dups = health.duplicate_codes || [];
  return (
    <div className="bg-white border border-border p-6 mt-8" data-testid="catalog-health">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-lg">Catalog health</h2>
        </div>
        <span
          data-testid="catalog-sync-badge"
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border ${
            inSync ? "border-green-600/30 bg-green-600/10 text-green-700" : "border-amber-500/40 bg-amber-500/10 text-amber-700"
          }`}
        >
          {inSync ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {inSync ? "Live matches shipped catalog" : "Live differs from shipped catalog — redeploy to sync"}
        </span>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-5">
        <Metric label="Live documents" value={health.total} testid="catalog-live-count" />
        <Metric label="Shipped catalog" value={health.seed_catalog_count ?? "—"} testid="catalog-seed-count" />
        <Metric label="Placeholder samples" value={health.samples} warn={health.samples > 0} testid="catalog-sample-count" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">By department</div>
          <div className="flex flex-wrap gap-2">
            {health.departments.map((d) => (
              <span key={d.name} className="text-xs bg-secondary px-2 py-1">{d.name} · {d.count}</span>
            ))}
          </div>
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mt-4 mb-2">By system</div>
          <div className="flex flex-wrap gap-2">
            {health.systems.map((s) => (
              <span key={s.code} className="text-xs bg-secondary px-2 py-1 font-mono">{s.code} · {s.count}</span>
            ))}
          </div>
        </div>
        <div data-testid="catalog-duplicates">
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
            Duplicate codes {dups.length > 0 && <span className="text-amber-700">({dups.length})</span>}
          </div>
          {dups.length === 0 ? (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" /> No duplicate codes — every /forms/&#123;code&#125; is reachable.
            </p>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Only one form per code is reachable by URL — fix in Documents → Edit.</p>
              {dups.map((d) => (
                <div key={d.code} className="flex items-center justify-between text-sm border-b border-border py-1">
                  <span className="font-mono">{d.code}</span>
                  <span className="text-amber-700 font-semibold">×{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, warn, testid }) {
  return (
    <div className="border border-border p-4">
      <div className={`font-heading font-black text-2xl ${warn ? "text-amber-700" : ""}`} data-testid={testid}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
